import http from 'utlis/http';

const designAuditApi = process.env.REACT_APP_DESIGN_AUDIT_BASE_URL;

/**
 * Infer product context from design and analytics screenshots.
 *
 * Sends design images as multipart file uploads (fetched from their S3 URLs)
 * and optional analytics image URLs as a JSON array string.
 *
 * Request: multipart/form-data
 *   - frame_image_0, frame_image_1, ... — design images (required, at least one)
 *   - analytics_image_urls              — JSON array string of analytics URLs (optional)
 *   - max_chars                         — max character length for generated fields
 *
 * Response: { title, audience, problem, business_goal }
 */
export const getAiContextSuggestions = async (imageUrls, analyticsImageUrls = null) => {
  if (!imageUrls || imageUrls.length === 0) {
    throw new Error('At least one design image URL is required');
  }

  const formData = new FormData();

  // Fetch each design image from its S3 URL, collect in order, then append
  const imageFiles = await Promise.all(
    imageUrls.map(async (url, index) => {
      const response = await fetch(url);
      const blob = await response.blob();
      const ext = blob.type ? blob.type.split('/')[1] : 'png';

      return new File([blob], `design_${index + 1}.${ext}`, { type: blob.type || 'image/png' });
    })
  );

  imageFiles.forEach((file, index) => {
    formData.append(`frame_image_${index}`, file);
  });

  // Pass analytics URLs as a JSON array string when provided
  const analyticsUrls = Array.isArray(analyticsImageUrls) && analyticsImageUrls.length > 0
    ? analyticsImageUrls
    : null;

  if (analyticsUrls && analyticsUrls.length > 0) {
    formData.append('analytics_image_urls', JSON.stringify(analyticsUrls));
  }

  formData.append('max_chars', '600');

  const { data } = await http.post(
    `${designAuditApi}/infer_context`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-BACKEND-URL': process.env.REACT_APP_X_BACKEND_URL,
      },
    }
  );

  const context = data.context || {};

  return {
    title: context.title || '',
    audience: context.target_audience || '',
    problem: context.problem_statement || '',
    businessGoal: context.business_goal || '',
  };
};
